/* Skincare quiz — the interface.
 *
 * The decision itself lives in quiz-engine.js and is deliberately kept out of this file:
 * this one only asks questions, holds answers, and renders what the engine returns.
 */
import { mountChrome } from './chrome.js?v=1789681238';
import { catalog, esc, productHref, effPrice, img, cdnTile } from './catalog.js?v=1789571604';
import { CART, money } from './cart.js?v=1789571604';
import { recommend, CONCERNS } from './quiz-engine.js?v=1789571604';

mountChrome();

const onShopify = () => typeof window !== 'undefined' && window.NEWEL_ON_SHOPIFY === true;
const base = () => (document.body && document.body.dataset.base) || '';

/* THE SIGNED-IN CUSTOMER, or null. Written into the page by the Liquid layout, which is
   the only place that can know. Used for three things: skip asking a name we already
   have, attach the answers to the right account, and choose the right way to record the
   result, because the two cases genuinely differ (see recordToShopify). */
const customer = () => (typeof window !== 'undefined' && window.NEWEL_CUSTOMER) || null;

/* A one-line, human-readable summary of the answers. This is what Priti reads in the
   Shopify admin, so it is written for a person, not for a parser. */
function answerSummary(a) {
  const label = {
    aging: 'fine lines', 'dark-spots': 'dark spots', acne: 'breakouts', dryness: 'dryness',
    dullness: 'dullness', sensitivity: 'sensitivity', sun: 'sun', 'post-treatment': 'post-treatment',
  };
  const bits = [
    `${a.skinType || 'unknown'} skin`,
    `main concern ${label[a.primary] || a.primary || 'unknown'}`,
  ];
  if ((a.secondary || []).length) bits.push(`also ${a.secondary.map(c => label[c] || c).join(' and ')}`);
  bits.push(`retinoid: ${a.vitaminA || 'unknown'}`);
  if ((a.flags || []).length) bits.push(`flags: ${a.flags.join(', ')}`);
  if (a.routineDepth) bits.push(`routine today: ${a.routineDepth}`);
  if (a.budget) bits.push(`budget: ${a.budget}`);
  return bits.join('; ');
}

/* RECORDING THE RESULT IN SHOPIFY.
 *
 * Cart attributes are the mechanism that actually works from a theme with no app behind
 * it. Whatever is on the cart when an order is placed is written onto that ORDER and shows
 * in the admin under the order's additional details, and because the order belongs to the
 * account, a signed-in customer's answers land on their account by that route.
 *
 * WHAT THIS DELIBERATELY DOES NOT CLAIM TO DO. Writing a customer METAFIELD, or adding a
 * tag to an ALREADY EXISTING customer, cannot be done from the storefront: both need the
 * Admin API and therefore a private app with a token, which does not exist for this store
 * yet. So for a signed-in customer this records the answers on the cart, which is real and
 * verifiable, rather than pretending to write to their profile. For a NEW visitor the
 * customer form does create a real record carrying the answers as tags.
 */
async function recordToShopify(a, picked) {
  if (!onShopify()) return { ok: false, reason: 'not-on-shopify' };
  const c = customer();
  const attrs = {
    'Skin quiz': answerSummary(a),
    'Skin quiz routine': picked.map(r => r.product.t).join(' | ') || 'no products matched',
    'Skin quiz taken': new Date().toISOString().slice(0, 10),
  };
  if (c && c.id) attrs['Skin quiz customer'] = String(c.id);
  try {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
    return { ok: res.ok };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

/* Answers survive a reload. Somebody who gets six questions deep, opens a product in a new
   tab and comes back should not be made to start again. */
const SAVE_KEY = 'lps-quiz-v1';
const save = a => { try { sessionStorage.setItem(SAVE_KEY, JSON.stringify(a)); } catch {} };
const load = () => { try { return JSON.parse(sessionStorage.getItem(SAVE_KEY) || 'null'); } catch { return null; } };
const clear = () => { try { sessionStorage.removeItem(SAVE_KEY); } catch {} };

/* --------------------------------------------------------------------- questions
   `key` is the answers field. `type` is 'text' | 'single' | 'multi'. A 'multi' question
   is always skippable, because "none of these" is a real answer and forcing a choice
   would put words in their mouth. */
const QUESTIONS = [
  {
    key: 'name', type: 'text', title: 'First, what should we call you?',
    help: 'Only so the routine reads like it was written for you rather than at you.',
    placeholder: 'First name', required: true,
  },
  {
    key: 'skinType', type: 'single', title: 'How does your skin behave on an average day?',
    help: 'Think about how it feels a few hours after you wash it, not how it feels right now.',
    options: [
      { v: 'oily',        t: 'Oily',        d: 'Shine comes back through by the afternoon, pores are easy to see.' },
      { v: 'combination', t: 'Combination', d: 'Oily through the forehead and nose, normal or dry on the cheeks.' },
      { v: 'normal',      t: 'Normal',      d: 'Comfortable most days, no strong tendency either way.' },
      { v: 'dry',         t: 'Dry',         d: 'Tight after cleansing, sometimes flaky, drinks up moisturiser.' },
      { v: 'sensitive',   t: 'Sensitive',   d: 'Flushes, stings or reacts easily to new products.' },
    ],
  },
  {
    key: 'primary', type: 'single', title: 'What bothers you most?',
    help: 'Pick the one thing you would fix first. This carries the most weight in what we recommend.',
    options: Object.entries(CONCERNS).map(([v, d]) => ({
      v, t: {
        aging: 'Fine lines and firmness', 'dark-spots': 'Dark spots and uneven tone',
        acne: 'Breakouts and congestion', dryness: 'Dryness and dehydration',
        dullness: 'Dullness and rough texture', sensitivity: 'Redness and sensitivity',
        sun: 'Sun damage and protection', 'post-treatment': 'Recovering from a treatment',
      }[v], d,
    })),
  },
  {
    key: 'secondary', type: 'multi', title: 'Anything else on your mind?',
    help: 'Optional. These are weighted, but they never outrank the one you just chose.',
    optionsFrom: 'primary',
  },
  {
    key: 'routineDepth', type: 'single', title: 'What does your routine look like today?',
    help: 'This decides how many steps we hand back. There is no wrong answer here.',
    options: [
      { v: 'none',  t: 'Barely anything', d: 'Soap and water, or nothing at all.' },
      { v: 'basic', t: 'Cleanse and moisturise', d: 'The basics, most days.' },
      { v: 'full',  t: 'A full routine', d: 'Several steps, and I am happy to add more.' },
    ],
  },
  {
    key: 'vitaminA', type: 'single', title: 'Have you used retinol or vitamin A?',
    help: 'This one genuinely matters. Strength is built up over months, and starting too high is the most common reason people give a retinoid up.',
    options: [
      { v: 'none',    t: 'Never',           d: 'Start me at the bottom of the range.' },
      { v: 'past',    t: 'I have before',   d: 'Not using one at the moment.' },
      { v: 'current', t: 'I use one now',   d: 'My skin is already used to it.' },
    ],
  },
  {
    key: 'flags', type: 'multi', title: 'Anything we should be careful about?',
    help: 'Optional, and it only ever removes things from your routine, never adds them.',
    options: [
      { v: 'pregnant',         t: 'Pregnant or breastfeeding', d: 'We will leave vitamin A and retinoids out entirely.' },
      { v: 'reactive',         t: 'My skin reacts easily',     d: 'No acids, and no retinoid to start on your own.' },
      { v: 'recent-treatment', t: 'Recent professional treatment', d: 'We will keep it gentle while your skin settles.' },
    ],
  },
  {
    key: 'eyes', type: 'single', title: 'Do you want an eye step?',
    options: [
      { v: 'yes', t: 'Yes', d: 'Add an eye product to the routine.' },
      { v: 'no',  t: 'No',  d: 'Skip it for now.' },
    ],
  },
  {
    key: 'budget', type: 'single', title: 'Where should we keep the prices?',
    help: 'A ceiling per product, not for the whole routine. If everything that suits you sits above it, we will say so and show you the closest match anyway.',
    options: [
      { v: 'value',   t: 'Under $60 a product',  d: 'Keep it lean.' },
      { v: 'mid',     t: 'Under $130 a product', d: 'Room for a treatment step.' },
      { v: 'premium', t: 'No ceiling',           d: 'Show me what is actually best.' },
    ],
  },
];

/* ------------------------------------------------------------------------- state */
let answers = load() || {};
let index = 0;
let products = [];
const root = document.querySelector('[data-quiz]');

const answered = q => {
  const v = answers[q.key];
  if (q.type === 'multi') return true;              // optional by design
  if (q.type === 'text') return !!(v && String(v).trim());
  return v !== undefined && v !== null && v !== '';
};

/* --------------------------------------------------------------------- rendering */
function optionsFor(q) {
  if (!q.optionsFrom) return q.options;
  /* "Anything else" offers every concern EXCEPT the one already chosen as primary, so the
     same answer can never be given twice and quietly double-count in the scoring. */
  return Object.entries(CONCERNS)
    .filter(([v]) => v !== answers[q.primaryKey || 'primary'])
    .map(([v, d]) => ({ v, t: d, d: '' }));
}

function questionHTML(q) {
  const pct = Math.round((index / QUESTIONS.length) * 100);
  const opts = optionsFor(q);
  let body;

  if (q.type === 'text') {
    body = `<div class="quiz__field">
        <label class="u-sr-only" for="q-${esc(q.key)}">${esc(q.title)}</label>
        <input class="quiz__input" id="q-${esc(q.key)}" type="text" autocomplete="given-name"
               maxlength="40" placeholder="${esc(q.placeholder || '')}"
               value="${esc(answers[q.key] || '')}" data-text>
      </div>`;
  } else {
    const sel = q.type === 'multi' ? (answers[q.key] || []) : [answers[q.key]];
    body = `<div class="quiz__options" role="group" aria-label="${esc(q.title)}">` + opts.map(o => `
        <button class="quiz__opt${sel.includes(o.v) ? ' is-on' : ''}" type="button"
                data-opt="${esc(o.v)}" aria-pressed="${sel.includes(o.v)}">
          <span class="quiz__opt-t">${esc(o.t)}</span>
          ${o.d ? `<span class="quiz__opt-d">${esc(o.d)}</span>` : ''}
        </button>`).join('') + `</div>`;
  }

  return `
    <div class="quiz__head">
      <div class="quiz__bar"><span style="width:${pct}%"></span></div>
      <p class="quiz__count" aria-live="polite">Question ${index + 1} of ${QUESTIONS.length}</p>
    </div>
    <h2 class="quiz__q" tabindex="-1">${esc(q.title)}</h2>
    ${q.help ? `<p class="quiz__help">${esc(q.help)}</p>` : ''}
    ${body}
    <div class="quiz__nav">
      <button class="btn btn--ghost" type="button" data-back ${index === 0 ? 'disabled' : ''}>Back</button>
      <button class="btn btn--primary" type="button" data-next>${index === QUESTIONS.length - 1 ? 'See my routine' : 'Continue'}</button>
    </div>
    <p class="quiz__err" data-err hidden></p>`;
}

function renderQuestion() {
  const q = QUESTIONS[index];
  root.innerHTML = questionHTML(q);

  /* FOCUS HAS TO BE MOVED BY HAND HERE.
     Each question replaces root.innerHTML, which destroys the element that had focus, and
     the browser then drops focus to <body>. A keyboard visitor pressing Tab from there
     starts again at the top of the document: measured at 63 focusable elements of header,
     mega menu, search and bag before the first answer on the new question. Moving focus to
     the heading, which carries tabindex="-1" so it can receive it, puts the next Tab on
     the first option and makes a screen reader read the new question. The count above is
     aria-live so progress is announced as it changes. */
  const textField = root.querySelector('[data-text]');
  (textField || root.querySelector('.quiz__q'))?.focus();

  root.querySelectorAll('[data-opt]').forEach(btn => btn.addEventListener('click', () => {
    const v = btn.dataset.opt;
    if (q.type === 'multi') {
      const cur = new Set(answers[q.key] || []);
      cur.has(v) ? cur.delete(v) : cur.add(v);
      answers[q.key] = [...cur];
      save(answers);
      renderQuestion();
    } else {
      answers[q.key] = v;
      save(answers);
      /* A single-choice answer advances on its own. Making somebody tap their answer and
         then tap Continue doubles the taps in a nine-question flow for no information. */
      next();
    }
  }));

  root.querySelector('[data-back]')?.addEventListener('click', () => {
    if (index > 0) { index--; renderQuestion(); }
  });
  root.querySelector('[data-next]')?.addEventListener('click', next);
  root.querySelector('[data-text]')?.addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); next(); }
  });
}

function next() {
  const q = QUESTIONS[index];
  if (q.type === 'text') {
    const el = root.querySelector('[data-text]');
    answers[q.key] = (el?.value || '').trim();
    save(answers);
  }
  /* Moving past a multi-select records the empty answer, so "I looked and chose nothing"
     is stored as [] and is distinguishable on resume from "never shown", which is
     undefined. Without this the two are the same and resume skips the question. */
  if (q.type === 'multi' && answers[q.key] === undefined) {
    answers[q.key] = [];
    save(answers);
  }
  if (q.required && !answered(q)) {
    const err = root.querySelector('[data-err]');
    if (err) { err.textContent = 'We just need something to call you.'; err.hidden = false; }
    root.querySelector('[data-text]')?.focus();
    return;
  }
  if (!answered(q)) {
    const err = root.querySelector('[data-err]');
    if (err) { err.textContent = 'Pick one to carry on.'; err.hidden = false; }
    return;
  }
  index++;
  if (index >= QUESTIONS.length) renderResults();
  else renderQuestion();
}

/* --------------------------------------------------------------------- results */
function productCardHTML(p, why, badge) {
  /* Route, price and image all come from catalog.js rather than being rebuilt here.
     Hand-rolling them is how this file first shipped and every one was wrong on the live
     store: `base() + 'product.html?h='` 404s once the theme flattens assets, `p.p` is the
     minimum across ALL variants including sold-out ones so it can print a price nobody can
     buy, and `p.tile` alone renders nothing at all for the merged-catalogue products that
     have no local cutout.

     NO loading="lazy" here. These cards are injected all at once when the quiz finishes,
     and the lazy load frequently never fired: testing saw one blank card on one run and
     every card blank on another, while the same URLs fetched fine at HTTP 200. The routine
     is the payoff for answering nine questions, so a shopper reaching it and seeing empty
     boxes is the worst possible moment for a broken image. A finished routine is at most
     six 300px thumbnails, so eager loading costs nothing worth having. */
  const src = p.tile ? base() + p.tile : cdnTile(img(p, 0));
  return `
    <article class="quiz-rec">
      <a class="quiz-rec__media" href="${productHref(p)}">
        ${src ? `<img src="${esc(src)}" alt="${esc(p.t)}" decoding="async" width="300" height="300">` : ''}
      </a>
      <div class="quiz-rec__body">
        ${badge ? `<p class="quiz-rec__badge">${esc(badge)}</p>` : ''}
        <p class="quiz-rec__brand">${esc(p.b || '')}</p>
        <h3 class="quiz-rec__name">
          <a href="${productHref(p)}">${esc(p.t)}</a>
        </h3>
        <p class="quiz-rec__price">${money(effPrice(p))}</p>
        <p class="quiz-rec__why">${esc(why)}</p>
        <button class="btn btn--primary quiz-rec__add" type="button" data-add="${esc(p.h)}">Add to bag</button>
      </div>
    </article>`;
}

function renderResults() {
  const { routine, notes } = recommend(normalise(answers), products);
  const picked = routine.filter(r => r.product);
  const total = picked.reduce((n, r) => n + Number(effPrice(r.product) || 0), 0);
  const first = (answers.name || '').trim();

  root.innerHTML = `
    <div class="quiz-res">
      <p class="eyebrow">Your routine</p>
      <h2 class="quiz-res__h">${first ? esc(first) + ', here is where we would start.' : 'Here is where we would start.'}</h2>
      <p class="lede">Built from your answers, in the order you would apply it. Every product
        below says why it is there. ${picked.length} ${picked.length === 1 ? 'product' : 'products'},
        ${money(total)} in total.</p>

      ${notes.length ? `<div class="quiz-res__notes">${notes.map(n => `<p>${esc(n)}</p>`).join('')}</div>` : ''}

      <ol class="quiz-res__steps">
        ${routine.map((r, i) => `
          <li class="quiz-step">
            <div class="quiz-step__label"><span>${i + 1}</span>${esc(r.label)}</div>
            ${r.product
              ? productCardHTML(r.product, r.why)
              : r.overBudget
                ? `<div class="quiz-step__empty"><p>${esc(r.why)}</p></div>
                   ${productCardHTML(r.overBudget.product, r.overBudget.why, 'Above the budget you set')}`
                : `<div class="quiz-step__empty"><p>${esc(r.why)}</p></div>`}
          </li>`).join('')}
      </ol>

      <div class="quiz-res__actions">
        <button class="btn btn--primary" type="button" data-add-all>Add the routine to my bag</button>
        <button class="btn btn--ghost" type="button" data-restart>Start over</button>
      </div>

      ${customer() ? `
      <div class="quiz-res__capture">
        <h3>Saved to your visit.</h3>
        <p>You are signed in as ${esc(customer().email || '')}, so your answers are attached
          to this cart. If you order, they travel with it and Priti can see exactly what you
          told us when she looks at your order.</p>
      </div>` : `
      <div class="quiz-res__capture">
        <h3>Want this routine emailed to you?</h3>
        <p>We will send it once, and add you to Skin Notes so you hear when Priti brings
          something new in. No pressure, and you can leave any time.</p>
        <form class="quiz-capture" data-capture novalidate>
          <input class="quiz__input" type="email" required placeholder="you@example.com"
                 aria-label="Email address" data-capture-email autocomplete="email">
          <button class="btn btn--primary" type="submit">Send it</button>
        </form>
        <p class="quiz-capture__err" data-capture-err hidden></p>
      </div>`}
    </div>`;

  /* CART.add(product, VARIANT). The second argument is a variant object, not a quantity.
     Passing 1 made `v` the number 1, which is truthy, so cart.js never looked up the real
     variant and `v.id` was undefined, which meant the `onShopify() && v.id` branch that
     actually POSTs to /cart/add.js was skipped entirely. Locally the bag drawer updated
     and the button said "Added", so it looked perfect in every manual click-through, while
     on the live store nothing ever reached Shopify's cart and checkout came up empty. */
  const variantOf = p => (p.v || []).find(x => x.av) || (p.v || [])[0];

  root.querySelectorAll('[data-add]').forEach(b => b.addEventListener('click', async () => {
    const p = products.find(x => x.h === b.dataset.add);
    if (!p || b.disabled) return;
    b.disabled = true;
    const res = await CART.add(p, variantOf(p));
    b.disabled = false;
    if (res && res.ok === false) {
      b.textContent = 'Unavailable';
      setTimeout(() => { b.textContent = 'Add to bag'; }, 2200);
      return;
    }
    b.textContent = 'Added';
    setTimeout(() => { b.textContent = 'Add to bag'; }, 1600);
  }));

  root.querySelector('[data-add-all]')?.addEventListener('click', async e => {
    const btn = e.currentTarget;
    if (btn.disabled) return;
    btn.disabled = true;
    btn.textContent = 'Adding...';
    let added = 0, failed = 0;
    for (const r of picked) {
      const res = await CART.add(r.product, variantOf(r.product));
      (res && res.ok === false) ? failed++ : added++;
    }
    btn.disabled = false;
    btn.textContent = failed
      ? `Added ${added}, ${failed} unavailable`
      : `Added ${added} to your bag`;
  });

  root.querySelector('[data-restart]')?.addEventListener('click', () => {
    clear(); answers = { secondary: [], flags: [] }; index = 0; renderQuestion();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  wireCapture(picked);
  root.querySelector('.quiz-res__h')?.setAttribute('tabindex', '-1');
  root.querySelector('.quiz-res__h')?.focus();
  window.scrollTo({ top: root.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });

  /* Record the answers against the store. Fire and forget: a visitor must never wait on
     it, and a failure here must never cost them their routine, which is already on screen
     by this point. */
  recordToShopify(normalise(answers), picked);
}

/* Email capture. On Shopify this is a REAL customer form POST, the same mechanism the
   footer newsletter and the welcome popup already use, so Shopify's own captcha attaches
   and a real customer record is created with consent recorded. A background fetch cannot
   satisfy that captcha and would look like it worked while silently dropping the signup.
   Off Shopify (local preview) there is nothing to post to, so it says so rather than
   pretending to send. */
function wireCapture(picked) {
  const form = root.querySelector('[data-capture]');
  const email = root.querySelector('[data-capture-email]');
  const err = root.querySelector('[data-capture-err]');
  /* Signed-in visitors get a confirmation panel instead of a capture form, so there is
     genuinely nothing to wire here. */
  if (!form || !email) return;

  const fail = msg => { if (err) { err.textContent = msg; err.hidden = false; } email.focus(); };

  if (onShopify()) {
    form.method = 'post';
    form.action = '/contact#contact_form';
    form.setAttribute('accept-charset', 'UTF-8');
    const add = (name, value) => {
      const i = document.createElement('input');
      i.type = 'hidden'; i.name = name; i.value = value;
      form.appendChild(i);
    };
    add('form_type', 'customer');
    add('utf8', '✓');
    /* Tags are how Priti can later tell a quiz signup from a footer one, and see which
       concern brought them in. Nothing here is a guess: both values were chosen by the
       visitor. */
    /* TAGS ARE THE DURABLE SIGNAL, and they are the only one proven against this store.
       contact[first_name] is a standard Shopify customer field so it is kept, but
       contact[note] is NOT part of form_type=customer: Shopify drops unrecognised fields
       silently, so shipping it would have looked like it recorded the routine while
       recording nothing. The information Priti actually needs to follow up, which concern
       brought them in and what skin they have, goes in tags where it survives. */
    add('contact[tags]', [
      'newsletter', 'skin-quiz',
      `concern-${answers.primary || 'unknown'}`,
      `skin-${answers.skinType || 'unknown'}`,
      `retinoid-${answers.vitaminA || 'unknown'}`,
    ].join(', '));
    if (answers.name) add('contact[first_name]', answers.name);
    email.name = 'contact[email]';
  }

  form.addEventListener('submit', e => {
    const v = (email.value || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v)) { e.preventDefault(); fail('That email does not look right.'); return; }
    if (!onShopify()) {
      e.preventDefault();
      fail('This is the local preview, so there is nothing to send to yet. It posts for real once the theme is live.');
    }
    /* on Shopify: let the native POST proceed */
  });
}

/* The engine wants `eyes` as a boolean and never wants undefined arrays. */
function normalise(a) {
  return { ...a, eyes: a.eyes === 'yes', secondary: a.secondary || [], flags: a.flags || [] };
}

/* -------------------------------------------------------------------- bootstrap */
(async function start() {
  if (!root) return;

  /* SIGNED IN: do not ask for something the store already knows. Asking a customer their
     first name on a page that is already greeting them by it is the kind of small
     thoughtlessness that makes an account feel pointless. The question is removed from the
     flow entirely rather than pre-filled and skipped, so the counter reads "of 8" and the
     progress bar is honest about how much is left. */
  const c = customer();
  if (c && c.first_name) {
    const i = QUESTIONS.findIndex(q => q.key === 'name');
    if (i !== -1) QUESTIONS.splice(i, 1);
    answers.name = c.first_name;
  }
  try {
    const cat = await catalog();
    /* Availability comes from the catalogue's own flag. The engine refuses anything
       without __buyable, so an unknown-stock product is skipped rather than recommended:
       being told to buy something that cannot be bought is worse than a shorter routine. */
    products = cat.map(p => ({ ...p, __buyable: p.av !== false }));
  } catch {
    root.innerHTML = `<div class="quiz__noscript">
      <h2>We could not load the shop just now.</h2>
      <p class="lede">Refresh and it will usually sort itself out. If it does not, email
        <a class="link-underline" href="mailto:hello@lepetitskincare.com">hello@lepetitskincare.com</a>
        and Priti will answer these questions herself.</p></div>`;
    return;
  }
  /* Resume mid-quiz if they left one running, but never resume straight onto results. */
  const saved = load();
  if (saved) {
    /* Do NOT re-seed { secondary: [], flags: [] } over the saved answers. Seeding made
       an untouched multi-select look answered, and the resume scan then walked straight
       past it. The one it walked past was "Anything we should be careful about?", the
       question carrying pregnant / reactive / recent-treatment, so a visitor who resumed
       got a routine computed as though they had said no to all three, and the engine's
       refusal to recommend retinoids and acids never fired. undefined has to keep meaning
       "never shown". */
    answers = saved;
    const firstUnanswered = QUESTIONS.findIndex(
      q => (q.type === 'multi' ? answers[q.key] === undefined : !answered(q)));
    index = firstUnanswered === -1 ? 0 : firstUnanswered;
  }
  renderQuestion();
})();
